/* BACKTEST LIBERO · lanciare una prova, singola o a griglia.
 *
 * Le nove schermate del disegno coprivano consultare, confrontare e mettere
 * live: mancava creare. Questa è quella che mancava, e ha tre problemi veri
 * dentro, che non sono «mostrare dei campi»:
 *
 *  1. I PARAMETRI SONO DINAMICI. Le strategie sono 65 e ne nascono altre:
 *     i campi si costruiscono da `GET /backtester/strategies`, che porta
 *     `defaultParams` e la descrizione. Un elenco fisso qui sarebbe già vecchio.
 *
 *  2. IL WARMUP SBAGLIATO NON DÀ ERRORE. Consuma l'inizio del periodo, e se è
 *     insufficiente il risultato è completo, plausibile e falso — misurato:
 *     2.400 invece di 2.700 dava 12,56% invece di 10,57%, cioè sembrava
 *     migliore. Il minimo si stima dai parametri con la stessa euristica del
 *     motore (`lib/warmup.ts`) e si propone con un tocco.
 *
 *  3. UNA GRIGLIA È UN PRODOTTO CARTESIANO. Quattro parametri con cinque
 *     valori fanno 625 backtest, e cinque ne fanno 3.125 senza che nulla
 *     cambi a schermo. Il conteggio delle combinazioni si vede MENTRE si
 *     compila, prima di lanciare.
 *
 * Il risultato di un'esecuzione singola non porta da nessun'altra parte: si
 * apre qui sotto, con lo stesso componente che usa il dettaglio.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Avvertenza,
  Avvisi,
  Campo,
  CampoSelettore,
  ContenitoreDati,
  Distintivo,
  Foglio,
  GruppoCampi,
  Modale,
  IconaStrategia,
  IntestazionePagina,
  Pulsante,
  Selettore,
  SelettoreStrategia,
  Verdetto,
  usaAvvisi,
} from '../../componenti';
import { backtest as apiBacktest, datiStorici, exchanges } from '../../api/risorse';
import { usaRichiesta } from '../../api/usaRichiesta';
import { dataOra, num, periodo as etichettaPeriodo, timeframe } from '../../lib/formato';
import { giorniDiWarmup, warmupRichiesto } from '../../lib/warmup';
import type {
  AvvioBacktest,
  AvvioGruppoBacktest,
  RangeDatiStorici,
  StrategiaDisponibile,
} from '../../api/tipi';
import { RisultatoBacktest, TernaDelRisultato } from './RisultatoBacktest';
import { VENUE, erosione } from './costi';
import './backtest.css';

/* I timeframe che il magazzino contiene davvero, in minuti. Le pillole sono un
   punto di partenza: il campo resta libero, perché il motore non ha una lista. */
const TIMEFRAME_COMUNI = [15, 30, 60, 120, 240, 360, 720, 1440];

/** Giri completi su cui si mostra l'erosione dei costi. Dichiarato, non finto:
 *  quante operazioni farà questa configurazione non si sa prima di eseguirla. */
const GIRI_DI_RIFERIMENTO = 120;

/** Oltre questo numero di combinazioni la griglia va guardata due volte. */
const COMBINAZIONI_MOLTE = 5000;
/** Oltre questo, quasi sempre è un errore di battitura. */
const COMBINAZIONI_TROPPE = 50000;

type Modo = 'singolo' | 'gruppo';

interface RigaTimeframe {
  candleSize: string;
  warmupPeriod: string;
}

interface Modulo {
  modo: Modo;
  exchangeId: string;
  asset: string;
  currency: string;
  startDate: string;
  endDate: string;
  strategyName: string;
  /** Sempre testo: in griglia lo stesso campo contiene «10, 20, 30». */
  parametri: Record<string, string>;
  candleSize: string;
  warmupPeriod: string;
  timeframe: RigaTimeframe[];
  assetBalance: string;
  currencyBalance: string;
  feeMode: 'MAKER' | 'TAKER';
  makerFee: string;
  takerFee: string;
  slippage: string;
  force: boolean;
  maxDrawdown: string;
  minWinRate: string;
  maxUnderperformance: string;
  maxTradesPerDay: string;
  earlyStopCheckpoint: string;
}

const MODULO_VUOTO: Modulo = {
  modo: 'singolo',
  exchangeId: '',
  asset: '',
  currency: '',
  startDate: '',
  endDate: '',
  strategyName: '',
  parametri: {},
  candleSize: '60',
  warmupPeriod: '',
  timeframe: [{ candleSize: '60', warmupPeriod: '' }],
  assetBalance: '0',
  currencyBalance: '1000',
  feeMode: 'TAKER',
  makerFee: '0',
  takerFee: '0',
  slippage: '0.025',
  force: false,
  maxDrawdown: '',
  minWinRate: '',
  maxUnderperformance: '',
  maxTradesPerDay: '',
  earlyStopCheckpoint: '',
};

/* --------------------------------------------------------------------------
 * Lettura dei valori
 * ------------------------------------------------------------------------ */

function numeroDa(testo: string): number | null {
  const t = testo.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/**
 * I valori di un parametro in griglia.
 *
 * Separatore la virgola, e in più la forma `da..a:passo` — perché una griglia
 * con venti valori scritti a mano si sbaglia, e scriverla come `10..200:10` no.
 * Il decimale si scrive col PUNTO: la virgola è già il separatore, e «0,5»
 * diventerebbe due valori senza dirlo. Per questo i valori riconosciuti si
 * rileggono sotto il campo: quello che il modulo ha capito deve essere
 * visibile prima di lanciare, non dopo.
 */
function valoriDaTesto(testo: string, esempio: unknown): unknown[] {
  const pezzi = testo
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const numerico = typeof esempio === 'number';
  const booleano = typeof esempio === 'boolean';
  const fuori: unknown[] = [];

  for (const pezzo of pezzi) {
    const intervallo = /^(-?[\d.]+)\.\.(-?[\d.]+)(?::(-?[\d.]+))?$/.exec(pezzo);
    if (intervallo && numerico) {
      const da = Number(intervallo[1]);
      const a = Number(intervallo[2]);
      const passo = Math.abs(Number(intervallo[3] ?? 1)) || 1;
      if (Number.isFinite(da) && Number.isFinite(a)) {
        /* Il limite non è estetico: `1..1000000` in un campo produrrebbe un
           milione di valori e bloccherebbe la scheda mentre si digita. */
        for (let v = da, i = 0; (da <= a ? v <= a : v >= a) && i < 2000; v += da <= a ? passo : -passo, i++) {
          fuori.push(Number(v.toFixed(6)));
        }
        continue;
      }
    }
    if (booleano) fuori.push(pezzo === 'true' || pezzo === '1');
    else if (numerico) {
      const n = Number(pezzo);
      if (Number.isFinite(n)) fuori.push(n);
    } else fuori.push(pezzo);
  }
  return fuori;
}

/** Il valore singolo di un parametro, col tipo del suo predefinito. */
function valoreDaTesto(testo: string, esempio: unknown): unknown {
  const t = testo.trim();
  if (typeof esempio === 'number') {
    const n = Number(t);
    return Number.isFinite(n) ? n : esempio;
  }
  if (typeof esempio === 'boolean') return t === 'true' || t === '1';
  return t;
}

/**
 * Legge una griglia incollata in JSON e la traduce nei campi del modulo.
 *
 * Rifiuta le chiavi che non appartengono alla strategia scelta invece di
 * passarle: il motore le ignorerebbe in silenzio, e si crederebbe di aver
 * lanciato una griglia che non è quella scritta.
 */
function leggiGrigliaJson(
  testo: string,
  strategia: StrategiaDisponibile | null,
): { parametri: Record<string, string>; errore: string | null } {
  if (!strategia) return { parametri: {}, errore: 'Scegli prima una strategia.' };
  let letto: unknown;
  try {
    letto = JSON.parse(testo);
  } catch {
    return { parametri: {}, errore: 'Non è JSON valido: controlla virgolette e parentesi.' };
  }
  if (letto == null || typeof letto !== 'object' || Array.isArray(letto)) {
    return { parametri: {}, errore: 'Serve un oggetto con una chiave per parametro.' };
  }

  const attese = new Set(Object.keys(strategia.defaultParams));
  const fuori: Record<string, string> = {};
  const estranee: string[] = [];
  for (const [chiave, valore] of Object.entries(letto as Record<string, unknown>)) {
    if (!attese.has(chiave)) {
      estranee.push(chiave);
      continue;
    }
    fuori[chiave] = Array.isArray(valore) ? valore.join(', ') : String(valore);
  }
  if (estranee.length > 0) {
    return {
      parametri: {},
      errore: `${strategia.name} non ha ${estranee.length === 1 ? 'il parametro' : 'i parametri'} ${estranee.join(', ')}: o la strategia è un'altra, o le chiavi sono scritte diversamente.`,
    };
  }
  if (Object.keys(fuori).length === 0) {
    return { parametri: {}, errore: 'Nessun parametro riconosciuto in questo JSON.' };
  }
  return { parametri: fuori, errore: null };
}

function siglaDi(nome: string): string {
  const maiuscole = nome.replace(/[^A-Z0-9]/g, '');
  if (maiuscole.length >= 2) return maiuscole.slice(0, 3);
  return nome.slice(0, 3).toUpperCase();
}

/* --------------------------------------------------------------------------
 * Precompilazione dalla query string
 * ------------------------------------------------------------------------ */

/**
 * Il modulo precompilato da un backtest esistente.
 *
 * Si arriva qui per rifare una prova cambiando una cosa sola, e ricopiare
 * quattordici campi a mano è il modo migliore per sbagliarne uno senza
 * accorgersene. I parametri di strategia viaggiano come JSON in `params`;
 * `p.<nome>` resta accettato per gli indirizzi scritti a mano.
 */
function daQueryString(p: URLSearchParams, base: Modulo): Modulo {
  const preso = (chiave: string, corrente: string): string => p.get(chiave) ?? corrente;
  const parametri: Record<string, string> = {};

  const grezzo = p.get('params');
  if (grezzo) {
    try {
      const oggetto = JSON.parse(grezzo) as Record<string, unknown>;
      for (const [k, v] of Object.entries(oggetto)) {
        parametri[k] = Array.isArray(v) ? v.join(', ') : String(v);
      }
    } catch {
      /* Un `params` illeggibile non deve svuotare il modulo: si ignora e i
         campi restano quelli della strategia scelta. */
    }
  }
  for (const [chiave, valore] of p.entries()) {
    if (chiave.startsWith('p.')) parametri[chiave.slice(2)] = valore;
  }

  const modo = p.get('modo') === 'gruppo' ? 'gruppo' : base.modo;
  const candleSize = preso('candleSize', base.candleSize);
  const warmupPeriod = preso('warmupPeriod', base.warmupPeriod);

  return {
    ...base,
    modo,
    exchangeId: preso('exchangeId', base.exchangeId),
    asset: preso('asset', base.asset),
    currency: preso('currency', base.currency),
    /* Le date arrivano ISO complete: il campo data vuole solo il giorno. */
    startDate: (p.get('startDate') ?? base.startDate).slice(0, 10),
    endDate: (p.get('endDate') ?? base.endDate).slice(0, 10),
    strategyName: preso('strategyName', base.strategyName),
    parametri,
    candleSize,
    warmupPeriod,
    timeframe: [{ candleSize, warmupPeriod }],
    assetBalance: preso('assetBalance', base.assetBalance),
    currencyBalance: preso('currencyBalance', base.currencyBalance),
    feeMode: p.get('feeMode') === 'MAKER' ? 'MAKER' : base.feeMode,
    makerFee: preso('makerFee', base.makerFee),
    takerFee: preso('takerFee', base.takerFee),
    slippage: preso('slippage', base.slippage),
  };
}

/* ========================================================================== */

export function NuovoBacktest() {
  const navigate = useNavigate();
  const [parametriUrl] = useSearchParams();
  const { avvisi, mostra } = usaAvvisi();

  /* `strategie` non prende un AbortSignal: l'endpoint legge la cartella delle
     strategie e risponde in millisecondi, quindi non c'e' niente da annullare. */
  const strategie = usaRichiesta(() => apiBacktest.strategie(), [], {
    eVuoto: (d) => d.length === 0,
  });
  const listaExchange = usaRichiesta(() => exchanges.elenco(), []);
  const storico = usaRichiesta((s) => datiStorici.schema(s), [], { eVuoto: () => false });

  const [modulo, setModulo] = useState<Modulo>(() => daQueryString(parametriUrl, MODULO_VUOTO));
  const [apertoId, setApertoId] = useState<number | null>(1);
  const [foglioStrategie, setFoglioStrategie] = useState(false);
  /* La griglia incollata in JSON: il frontend precedente aveva un modulo
     apposta («Group Raw»), ed è il modo in cui arrivano le griglie prodotte
     dalle pipeline di ricerca. Riscriverle a mano campo per campo sarebbe
     lavoro inutile e un'occasione di sbagliare un valore. */
  const [jsonAperto, setJsonAperto] = useState(false);
  const [jsonTesto, setJsonTesto] = useState('');
  const [jsonErrore, setJsonErrore] = useState<string | null>(null);
  const [cerca, setCerca] = useState('');
  const [inAvvio, setInAvvio] = useState(false);
  /* Quale finestra preimpostata e' in vigore. Si spegne appena una data viene
     toccata a mano: un pulsante che resta acceso mentre le date dicono altro
     afferma una cosa falsa. */
  const [preimpostato, setPreimpostato] = useState<ChiavePeriodo | null>(null);
  /** Id del backtest singolo lanciato: da qui parte il polling. */
  const [idAvviato, setIdAvviato] = useState<number | null>(null);
  /** `true` quando il server ha riusato una riga già esistente invece di calcolare. */
  const [riusato, setRiusato] = useState(false);
  const [gruppoAvviato, setGruppoAvviato] = useState<number | null>(null);

  const cambia = useCallback((patch: Partial<Modulo>) => setModulo((m) => ({ ...m, ...patch })), []);

  const strategia: StrategiaDisponibile | null = useMemo(
    () => strategie.dati?.find((s) => s.name === modulo.strategyName) ?? null,
    [strategie.dati, modulo.strategyName],
  );

  /* --- primo exchange come predefinito: senza, il modulo parte con un campo
     obbligatorio vuoto e l'errore compare solo al lancio. --- */
  useEffect(() => {
    if (modulo.exchangeId || !listaExchange.dati?.data.length) return;
    cambia({ exchangeId: String(listaExchange.dati.data[0].id) });
  }, [listaExchange.dati, modulo.exchangeId, cambia]);

  /* --- scegliendo una strategia i campi diventano quelli SUOI, con i valori
     predefiniti del motore. Quelli già scritti a mano per la stessa strategia
     non si perdono: si perderebbe la precompilazione arrivata dall'URL. --- */
  useEffect(() => {
    if (!strategia) return;
    setModulo((m) => {
      const nuovi: Record<string, string> = {};
      for (const [chiave, valore] of Object.entries(strategia.defaultParams)) {
        nuovi[chiave] = m.parametri[chiave] ?? String(valore);
      }
      return { ...m, parametri: nuovi };
    });
  }, [strategia]);

  /* --- il magazzino delle candele: quali coppie esistono e fin dove --- */
  const coppie = useMemo(() => raggruppaPerCoppia(storico.dati ?? []), [storico.dati]);
  const coppiaScelta = coppie.find(
    (c) =>
      String(c.exchangeId) === modulo.exchangeId &&
      c.asset === modulo.asset &&
      c.currency === modulo.currency,
  );

  /* --- warmup: il minimo richiesto dai parametri scelti --- */
  const parametriPerWarmup = useMemo(() => {
    if (!strategia) return {};
    const fuori: Record<string, unknown> = {};
    for (const [chiave, valore] of Object.entries(strategia.defaultParams)) {
      const testo = modulo.parametri[chiave] ?? String(valore);
      fuori[chiave] =
        modulo.modo === 'gruppo' ? valoriDaTesto(testo, valore) : valoreDaTesto(testo, valore);
    }
    return fuori;
  }, [strategia, modulo.parametri, modulo.modo]);

  const richiesta = useMemo(() => warmupRichiesto(parametriPerWarmup), [parametriPerWarmup]);

  /* --- combinazioni: il prodotto cartesiano, contato mentre si compila --- */
  const combinazioni = useMemo(() => {
    if (modulo.modo !== 'gruppo' || !strategia) return null;
    let prodotto = 1;
    const perParametro: { chiave: string; quanti: number }[] = [];
    for (const [chiave, esempio] of Object.entries(strategia.defaultParams)) {
      const quanti = valoriDaTesto(modulo.parametri[chiave] ?? String(esempio), esempio).length;
      perParametro.push({ chiave, quanti });
      prodotto *= Math.max(quanti, 0);
    }
    const righe = modulo.timeframe.length;
    return { totale: prodotto * righe, perParametro, righe };
  }, [modulo.modo, modulo.parametri, modulo.timeframe, strategia]);

  /* --- validazione: ciò che impedisce di lanciare, detto prima --- */
  const errori = useMemo(
    () => validazione(modulo, strategia, combinazioni?.totale ?? null),
    [modulo, strategia, combinazioni],
  );

  /* --- il polling dell'esecuzione singola. È lo stesso meccanismo del
     dettaglio: la riga torna subito in stato RUNNING e il risultato arriva
     interrogando `GET /backtester/:id` finché lo stato cambia. --- */
  const esecuzione = usaRichiesta(
    (segnale) => apiBacktest.uno(idAvviato as number, segnale),
    [idAvviato],
    { attiva: idAvviato != null },
  );
  const inCorso = esecuzione.dati?.status === 'RUNNING';
  const ricaricaEsecuzione = esecuzione.ricarica;
  useEffect(() => {
    if (!inCorso) return;
    const t = setInterval(ricaricaEsecuzione, 5000);
    return () => clearInterval(t);
  }, [inCorso, ricaricaEsecuzione]);

  /* --- avvio --- */
  const avvia = useCallback(async () => {
    if (!strategia || errori.length > 0) return;
    setInAvvio(true);
    try {
      if (modulo.modo === 'singolo') {
        const corpo = corpoSingolo(modulo, strategia);
        const riga = await apiBacktest.avvia(corpo);
        setIdAvviato(riga.id);
        /* Senza `force` il server restituisce una riga identica già esistente
           invece di ricalcolare. Non è un errore, ma va detto: i numeri che
           compaiono sono di un'altra esecuzione, con costi che possono essere
           diversi da quelli appena impostati. */
        setRiusato(riga.status !== 'RUNNING');
        mostra(riga.status === 'RUNNING' ? `Backtest #${riga.id} avviato` : `Backtest #${riga.id} già esistente`);
      } else {
        const gruppo = await apiBacktest.avviaGruppo(corpoGruppo(modulo, strategia));
        setGruppoAvviato(gruppo.id);
        mostra(`Gruppo #${gruppo.id} avviato`);
      }
    } catch (e) {
      mostra(e instanceof Error ? e.message : 'Avvio non riuscito', 'fallito');
    } finally {
      setInAvvio(false);
    }
  }, [modulo, strategia, errori, mostra]);

  const elencoStrategie = useMemo(() => {
    const q = cerca.trim().toLowerCase();
    return (strategie.dati ?? [])
      .filter((s) => !q || s.name.toLowerCase().includes(q))
      .map((s) => ({
        nome: s.name,
        sigla: siglaDi(s.name),
        numeroParametri: Object.keys(s.defaultParams).length,
        descrizione: s.descrizione ?? 'Nessuna descrizione: il motore non ne dichiara una per questa strategia.',
      }));
  }, [strategie.dati, cerca]);

  const periodoScelto = etichettaPeriodo(modulo.startDate, modulo.endDate);

  /* Il warmup «in vigore»: in griglia non è quello del campo singolo ma quello
     delle righe, e fra le righe conta la PEGGIORE — la combinazione con il
     warmup più basso è quella che rischia di partire con gli indicatori
     freddi, e un avviso calcolato sulla media non la vedrebbe. */
  const inVigore = useMemo(() => {
    if (modulo.modo === 'singolo') {
      return {
        warmup: numeroDa(modulo.warmupPeriod),
        candleSize: numeroDa(modulo.candleSize),
      };
    }
    const righe = modulo.timeframe
      .map((r) => ({ warmup: numeroDa(r.warmupPeriod), candleSize: numeroDa(r.candleSize) }))
      .filter((r) => r.warmup != null);
    if (righe.length === 0) return { warmup: null, candleSize: numeroDa(modulo.timeframe[0]?.candleSize ?? '') };
    return righe.reduce((peggiore, r) => ((r.warmup as number) < (peggiore.warmup as number) ? r : peggiore));
  }, [modulo.modo, modulo.warmupPeriod, modulo.candleSize, modulo.timeframe]);

  const giorniWarmup = giorniDiWarmup(inVigore.warmup ?? 0, inVigore.candleSize ?? 0);

  return (
    <div className="sb-cornice">
      <Avvisi avvisi={avvisi} />

      <IntestazionePagina
        titolo="Nuovo backtest"
        sotto={
          <>
            <Distintivo tono="accento">
              {modulo.modo === 'singolo' ? 'esecuzione singola' : 'griglia di parametri'}
            </Distintivo>
            <span className="sb-nota">
              {strategie.dati ? `${num(strategie.dati.length)} strategie disponibili` : 'strategie in arrivo…'}
            </span>
          </>
        }
        azioni={
          <Pulsante variante="tenue" onClick={() => navigate('/backtest')}>
            Tutti i backtest
          </Pulsante>
        }
      />

      <div className="sb-nuovo">
        {/* --- modo: due esecuzioni diverse, non una casella di spunta --- */}
        <div className="sb-nuovo__modi" role="radiogroup" aria-label="Tipo di esecuzione">
          {(['singolo', 'gruppo'] as Modo[]).map((m) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={modulo.modo === m}
              className={`sb-nuovo__modo${modulo.modo === m ? ' sb-nuovo__modo--scelto' : ''}`}
              onClick={() => cambia({ modo: m })}
            >
              <span className="sb-nuovo__modo-nome">
                {m === 'singolo' ? 'Un backtest' : 'Una griglia'}
              </span>
              <span className="sb-nuovo__modo-nota">
                {m === 'singolo'
                  ? 'Una configurazione, un risultato, con grafici e operazioni qui sotto.'
                  : 'Tutte le combinazioni dei valori indicati. Il risultato si guarda nei gruppi.'}
              </span>
            </button>
          ))}
        </div>

        <ContenitoreDati
          stato={strategie.stato}
          causaVuoto="Il motore non ha restituito nessuna strategia: senza, non c'è niente da lanciare."
          messaggioErrore={strategie.errore?.message}
          onRiprova={strategie.ricarica}
        >
          <>
            {/* ---------- 1. mercato e periodo ---------- */}
            <GruppoCampi
              numero={1}
              titolo="Mercato e periodo"
              conteggio={
                modulo.asset && modulo.currency
                  ? `${modulo.asset}/${modulo.currency} · ${periodoScelto}`
                  : 'da scegliere'
              }
              aperto={apertoId === 1}
              onApri={() => setApertoId(apertoId === 1 ? null : 1)}
            >
              {/* Il passo ha tre nature diverse, e ognuna ha la sua riga:
                  i CAMPI (che si compilano), le SCORCIATOIE (che li riempiono
                  al posto nostro) e le NOTE (che commentano ciò che è stato
                  scelto). Mescolarle in una riga sola è ciò che faceva finire
                  «Valuta» sopra «Finestra» e la nota sulle candele sopra il
                  campo «Al». */}

              {/* CAMPI · quale mercato. Tre in fila dove ci stanno, impilati
                  sul telefono: quante colonne le decide lo spazio, non un
                  breakpoint scritto qui. */}
              <div className="sb-griglia sb-griglia--stretta">
                <Selettore
                  etichetta="Exchange"
                  valore={modulo.exchangeId}
                  onCambia={(v) => cambia({ exchangeId: v })}
                  voci={(listaExchange.dati?.data ?? []).map((e) => ({
                    valore: String(e.id),
                    etichetta: e.name,
                    motivoSpento: e.isCCXTSupported === false ? 'non più supportato da ccxt' : undefined,
                  }))}
                />
                <Campo
                  etichetta="Asset"
                  testuale
                  value={modulo.asset}
                  placeholder="ETH"
                  onChange={(e) => cambia({ asset: e.target.value.toUpperCase() })}
                />
                <Campo
                  etichetta="Valuta"
                  testuale
                  value={modulo.currency}
                  placeholder="USDT"
                  onChange={(e) => cambia({ currency: e.target.value.toUpperCase() })}
                />
              </div>

              {/* SCORCIATOIA · riempie i tre campi qui sopra in un tocco, e per
                  questo sta sotto di essi e non in mezzo. */}
              <ElencoCoppie
                coppie={coppie}
                exchangeId={modulo.exchangeId}
                asset={modulo.asset}
                currency={modulo.currency}
                statoStorico={storico.stato}
                onScegli={(c) =>
                  cambia({
                    exchangeId: String(c.exchangeId),
                    asset: c.asset,
                    currency: c.currency,
                    startDate: modulo.startDate || c.dal.slice(0, 10),
                    endDate: modulo.endDate || c.al.slice(0, 10),
                  })
                }
              />

              {/* CAMPI · quale periodo. */}
              <div className="sb-griglia sb-griglia--stretta">
                <Campo
                  etichetta="Dal"
                  type="date"
                  value={modulo.startDate}
                  onChange={(e) => {
                    setPreimpostato(null);
                    cambia({ startDate: e.target.value });
                  }}
                />
                <Campo
                  etichetta="Al"
                  type="date"
                  value={modulo.endDate}
                  onChange={(e) => {
                    setPreimpostato(null);
                    cambia({ endDate: e.target.value });
                  }}
                />
              </div>

              {/* SCORCIATOIA · le finestre preimpostate riempiono le due date
                  qui sopra, quindi vengono dopo di esse. */}
              <FinestrePeriodo
                scelta={preimpostato}
                primaCandela={coppiaScelta?.dal ?? null}
                ultimaCandela={coppiaScelta?.al ?? null}
                onScegli={(chiave, dal, al) => {
                  setPreimpostato(chiave);
                  cambia({ startDate: dal, endDate: al });
                }}
              />

              {/* NOTE · testo lungo che commenta le scelte fatte sopra: riga
                  propria, piena larghezza. Se non c'è niente da dire il
                  contenitore sparisce (`:empty` nel CSS) e non lascia divari. */}
              <div className="sb-nuovo__note">
                {coppiaScelta && (
                  <CoperturaCandele
                    coppia={coppiaScelta}
                    dal={modulo.startDate}
                    al={modulo.endDate}
                  />
                )}
                <AvvisoFinestraMangiata
                  giorniWarmup={giorniWarmup}
                  dal={modulo.startDate}
                  al={modulo.endDate}
                />
              </div>
            </GruppoCampi>

            {/* ---------- 2. strategia e parametri ---------- */}
            <GruppoCampi
              numero={2}
              titolo="Strategia e parametri"
              conteggio={
                strategia
                  ? `${strategia.name} · ${Object.keys(strategia.defaultParams).length} parametri`
                  : 'da scegliere'
              }
              aperto={apertoId === 2}
              onApri={() => setApertoId(apertoId === 2 ? null : 2)}
            >
              <CampoSelettore
                etichetta="Strategia"
                suggerimento={strategie.dati ? `${num(strategie.dati.length)} disponibili` : undefined}
                valore={modulo.strategyName || 'Scegli fra le strategie del motore'}
                attivo={Boolean(modulo.strategyName)}
                onApri={() => setFoglioStrategie(true)}
              />

              {/* La descrizione è scritta per chi deve decidere: sta a schermo,
                  non in un suggerimento al passaggio del mouse. */}
              {strategia && (
                <div className="sb-nuovo__descrizione">
                  <div className="sb-nuovo__descrizione-testata">
                    <IconaStrategia nome={strategia.name} dim={26} />
                    <span className="sb-nuovo__descrizione-nome">{strategia.name}</span>
                  </div>
                  <p className="sb-nuovo__descrizione-testo">
                    {strategia.descrizione ??
                      'Il motore non dichiara una descrizione per questa strategia: quello che fa si legge solo nel suo codice.'}
                  </p>
                </div>
              )}

              {modulo.modo === 'gruppo' && strategia && (
                <Pulsante variante="tenue" onClick={() => setJsonAperto(true)}>
                  Incolla una griglia in JSON
                </Pulsante>
              )}

              {modulo.modo === 'gruppo' && strategia && (
                <Avvertenza livello="nota">
                  In una griglia ogni parametro è una <b>lista</b>, anche quando ha un valore solo.
                  Separa con la virgola — «10, 20, 30» — oppure scrivi un intervallo:{' '}
                  <b>10..200:10</b> vale venti valori. I decimali si scrivono col punto, perché la
                  virgola è già il separatore. Sotto ogni campo trovi scritto quanti valori sono
                  stati riconosciuti.
                </Avvertenza>
              )}

              {/* I parametri sono da due a dieci e sono tutti della stessa
                  natura: stanno in una griglia che si adatta, non uno sotto
                  l'altro. Il minimo di colonna è quello largo e non quello
                  stretto perché il suggerimento — «12 valori: 10, 20, 30…» —
                  è lungo e deve restare leggibile. */}
              {strategia && (
                <div className="sb-griglia">
                  {Object.entries(strategia.defaultParams).map(([chiave, predefinito]) => (
                    <CampoParametro
                      key={chiave}
                      chiave={chiave}
                      predefinito={predefinito}
                      valore={modulo.parametri[chiave] ?? String(predefinito)}
                      modo={modulo.modo}
                      onCambia={(v) =>
                        setModulo((m) => ({ ...m, parametri: { ...m.parametri, [chiave]: v } }))
                      }
                    />
                  ))}
                </div>
              )}

              {!strategia && (
                <div className="sb-nota">
                  I campi dei parametri compaiono dopo aver scelto la strategia: ogni strategia ha i
                  suoi, e il motore ne dichiara i valori predefiniti.
                </div>
              )}
            </GruppoCampi>

            {/* ---------- 3. timeframe e warmup ---------- */}
            <GruppoCampi
              numero={3}
              titolo="Candela e warmup"
              conteggio={
                modulo.modo === 'singolo'
                  ? `${timeframe(numeroDa(modulo.candleSize) ?? 0)} · warmup ${modulo.warmupPeriod || '—'}`
                  : `${modulo.timeframe.length} combinazioni di candela`
              }
              aperto={apertoId === 3}
              onApri={() => setApertoId(apertoId === 3 ? null : 3)}
            >
              <AvvisoWarmup
                minimo={richiesta.minimo}
                cause={richiesta.cause}
                impostato={inVigore.warmup}
                giorni={giorniWarmup}
                candleSize={inVigore.candleSize}
                onUsaMinimo={() =>
                  modulo.modo === 'singolo'
                    ? cambia({ warmupPeriod: String(richiesta.minimo) })
                    : cambia({
                        timeframe: modulo.timeframe.map((r) => ({
                          ...r,
                          warmupPeriod: String(richiesta.minimo),
                        })),
                        warmupPeriod: String(richiesta.minimo),
                      })
                }
              />

              {modulo.modo === 'singolo' ? (
                <>
                  <PillolePeriodo
                    valore={modulo.candleSize}
                    onScegli={(v) => cambia({ candleSize: v })}
                  />
                  <div className="sb-nuovo__coppia-campi">
                    <Campo
                      etichetta="Candela"
                      unita="min"
                      type="number"
                      min={1}
                      inputMode="numeric"
                      value={modulo.candleSize}
                      onChange={(e) => cambia({ candleSize: e.target.value })}
                    />
                    <Campo
                      etichetta="Warmup"
                      unita="candele"
                      type="number"
                      min={0}
                      inputMode="numeric"
                      suggerimento={richiesta.minimo > 0 ? `minimo ${num(richiesta.minimo)}` : undefined}
                      value={modulo.warmupPeriod}
                      onChange={(e) => cambia({ warmupPeriod: e.target.value })}
                    />
                  </div>
                </>
              ) : (
                <RigheTimeframe
                  righe={modulo.timeframe}
                  minimo={richiesta.minimo}
                  onCambia={(righe) => cambia({ timeframe: righe })}
                />
              )}
            </GruppoCampi>

            {/* ---------- 4. capitale e costi ---------- */}
            <GruppoCampi
              numero={4}
              titolo="Capitale e costi di esecuzione"
              conteggio={
                modulo.modo === 'gruppo'
                  ? 'non impostabili in griglia'
                  : `slippage ${modulo.slippage || '0'}% · fee ${modulo.feeMode.toLowerCase()}`
              }
              aperto={apertoId === 4}
              onApri={() => setApertoId(apertoId === 4 ? null : 4)}
            >
              {modulo.modo === 'gruppo' ? (
                /* Il DTO del gruppo non ha né capitale né costi: il comando di
                   griglia usa i suoi predefiniti. Dirlo è meglio che mostrare
                   campi che verrebbero ignorati. */
                <Avvertenza livello="nota">
                  Una griglia non accetta capitale e costi: il motore la esegue con i propri
                  predefiniti — 0 asset, 1.000 di valuta, nessuno slippage e le commissioni
                  dell'exchange. Per misurare una configurazione con i costi veri, rilanciala poi
                  come backtest singolo.
                </Avvertenza>
              ) : (
                <>
                  <div className="sb-nuovo__coppia-campi">
                    <Campo
                      etichetta="Capitale in valuta"
                      type="number"
                      inputMode="decimal"
                      value={modulo.currencyBalance}
                      onChange={(e) => cambia({ currencyBalance: e.target.value })}
                    />
                    <Campo
                      etichetta="Capitale in asset"
                      type="number"
                      inputMode="decimal"
                      value={modulo.assetBalance}
                      onChange={(e) => cambia({ assetBalance: e.target.value })}
                    />
                  </div>

                  <ScelteCosti
                    onApplica={(v) =>
                      cambia({
                        slippage: String(v.slippage),
                        makerFee: String(v.makerFee),
                        takerFee: String(v.takerFee),
                        feeMode: v.feeMode,
                      })
                    }
                  />

                  <div className="sb-nuovo__coppia-campi">
                    <Campo
                      etichetta="Slippage"
                      unita="%"
                      type="number"
                      step="0.001"
                      inputMode="decimal"
                      value={modulo.slippage}
                      onChange={(e) => cambia({ slippage: e.target.value })}
                    />
                    <Selettore
                      etichetta="Commissione applicata"
                      valore={modulo.feeMode}
                      onCambia={(v) => cambia({ feeMode: v === 'MAKER' ? 'MAKER' : 'TAKER' })}
                      voci={[
                        { valore: 'TAKER', etichetta: 'Taker' },
                        { valore: 'MAKER', etichetta: 'Maker' },
                      ]}
                    />
                  </div>

                  <div className="sb-nuovo__coppia-campi">
                    <Campo
                      etichetta="Commissione maker"
                      unita="%"
                      type="number"
                      step="0.001"
                      inputMode="decimal"
                      value={modulo.makerFee}
                      onChange={(e) => cambia({ makerFee: e.target.value })}
                    />
                    <Campo
                      etichetta="Commissione taker"
                      unita="%"
                      type="number"
                      step="0.001"
                      inputMode="decimal"
                      value={modulo.takerFee}
                      onChange={(e) => cambia({ takerFee: e.target.value })}
                    />
                  </div>

                  <label className="sb-nuovo__spunta">
                    <input
                      type="checkbox"
                      checked={modulo.force}
                      onChange={(e) => cambia({ force: e.target.checked })}
                    />
                    <span>
                      <b>Ricalcola anche se esiste già.</b> Con gli stessi parametri sulle stesse
                      candele il server restituisce la riga che ha in archivio invece di rifare il
                      calcolo — e quella riga può essere stata misurata con costi diversi da questi.
                    </span>
                  </label>
                </>
              )}
            </GruppoCampi>

            {/* ---------- 5. arresto anticipato (solo griglia) ---------- */}
            {modulo.modo === 'gruppo' && (
              <GruppoCampi
                numero={5}
                titolo="Arresto anticipato"
                conteggio="facoltativo"
                aperto={apertoId === 5}
                onApri={() => setApertoId(apertoId === 5 ? null : 5)}
              >
                <Avvertenza livello="cautela">
                  Fermare una combinazione a metà fa risparmiare ore, ma la riga che ne esce misura
                  un periodo più corto di quello richiesto: le sue metriche non sono confrontabili
                  con quelle delle righe arrivate in fondo. Lascia i campi vuoti se la griglia serve
                  a confrontare.
                </Avvertenza>
                <div className="sb-nuovo__coppia-campi">
                  <Campo
                    etichetta="Drawdown massimo"
                    unita="%"
                    type="number"
                    inputMode="decimal"
                    value={modulo.maxDrawdown}
                    onChange={(e) => cambia({ maxDrawdown: e.target.value })}
                  />
                  <Campo
                    etichetta="Quota vincenti minima"
                    suggerimento="da 0 a 1"
                    type="number"
                    step="0.01"
                    inputMode="decimal"
                    value={modulo.minWinRate}
                    onChange={(e) => cambia({ minWinRate: e.target.value })}
                  />
                </div>
                <div className="sb-nuovo__coppia-campi">
                  <Campo
                    etichetta="Ritardo sul mercato massimo"
                    suggerimento="da 0 a 1"
                    type="number"
                    step="0.01"
                    inputMode="decimal"
                    value={modulo.maxUnderperformance}
                    onChange={(e) => cambia({ maxUnderperformance: e.target.value })}
                  />
                  <Campo
                    etichetta="Operazioni al giorno massime"
                    type="number"
                    inputMode="decimal"
                    value={modulo.maxTradesPerDay}
                    onChange={(e) => cambia({ maxTradesPerDay: e.target.value })}
                  />
                </div>
                <Campo
                  etichetta="Da che punto controllare"
                  suggerimento="da 0,1 a 0,9 del periodo"
                  type="number"
                  step="0.05"
                  inputMode="decimal"
                  value={modulo.earlyStopCheckpoint}
                  onChange={(e) => cambia({ earlyStopCheckpoint: e.target.value })}
                />
              </GruppoCampi>
            )}

            {/* ---------- il lancio ---------- */}
            <div className="sb-nuovo__lancio">
              {combinazioni && (
                <ConteggioCombinazioni conteggio={combinazioni} />
              )}

              {modulo.modo === 'singolo' && numeroDa(modulo.slippage) != null && (
                <div className="sb-nota">
                  Con questi costi, su {GIRI_DI_RIFERIMENTO} giri completi l'erosione sarebbe del{' '}
                  <b>
                    {num(
                      erosione(
                        (numeroDa(modulo.slippage) ?? 0) * 100 +
                          (modulo.feeMode === 'MAKER'
                            ? (numeroDa(modulo.makerFee) ?? 0) * 100
                            : (numeroDa(modulo.takerFee) ?? 0) * 100),
                        GIRI_DI_RIFERIMENTO,
                      ) ?? 0,
                      1,
                    )}
                    %
                  </b>
                  . Quanti giri farà davvero questa configurazione non si sa prima di eseguirla:{' '}
                  {GIRI_DI_RIFERIMENTO} è solo un riferimento per confrontare i venue.
                </div>
              )}

              {errori.length > 0 && (
                <Avvertenza livello="grave">
                  <b>Manca qualcosa per lanciare:</b> {errori.join(' · ')}
                </Avvertenza>
              )}

              <Pulsante
                variante="primario"
                largo
                disabled={inAvvio || errori.length > 0}
                onClick={avvia}
              >
                {inAvvio
                  ? 'Avvio in corso…'
                  : modulo.modo === 'singolo'
                    ? 'Lancia il backtest'
                    : `Lancia la griglia${combinazioni ? ` · ${num(combinazioni.totale)} backtest` : ''}`}
              </Pulsante>
            </div>

            {/* ---------- l'esito ---------- */}
            {gruppoAvviato != null && (
              <SeguiGruppo id={gruppoAvviato} previsti={combinazioni?.totale ?? null} />
            )}

            {idAvviato != null && (
              <div className="sb-nuovo__esito">
                {riusato && (
                  <Avvertenza livello="cautela">
                    Il server ha restituito un backtest identico che aveva già in archivio invece di
                    rifare il calcolo: i numeri qui sotto sono di quell'esecuzione, e i costi con cui
                    è stata misurata possono essere diversi da quelli appena impostati. Per
                    ricalcolare davvero, spunta «Ricalcola anche se esiste già» e rilancia.
                  </Avvertenza>
                )}

                {inCorso && (
                  <Avvertenza livello="cautela">
                    Backtest #{idAvviato} in esecuzione: il motore sta calcolando e questa pagina si
                    aggiorna da sola ogni cinque secondi. Puoi lasciarla aperta.
                  </Avvertenza>
                )}

                {esecuzione.dati?.status === 'ERROR' && (
                  <Avvertenza livello="grave">
                    Il backtest #{idAvviato} si è interrotto con un errore
                    {esecuzione.dati.stopNotes ? `: ${esecuzione.dati.stopNotes}` : '.'} Le metriche
                    che eventualmente compaiono non descrivono il periodo intero.
                  </Avvertenza>
                )}

                {esecuzione.dati && (
                  <>
                    <div className="sb-nuovo__esito-testata">
                      <span className="sb-bt-riquadro__titolo">
                        Risultato · backtest #{esecuzione.dati.id}
                      </span>
                      <Pulsante onClick={() => navigate(`/backtest/${esecuzione.dati?.id}`)}>
                        Apri il dettaglio
                      </Pulsante>
                    </div>

                    {/* Il profitto non compare mai da solo: verdetto contro il
                        mercato, e accanto drawdown e operazioni. */}
                    <Verdetto
                      profitto={esecuzione.dati.strategyChange}
                      mercato={esecuzione.dati.marketChange}
                      drawdown={esecuzione.dati.drawdownMaxValue}
                      operazioni={esecuzione.dati.tradesNumber}
                      periodo={etichettaPeriodo(esecuzione.dati.startDate, esecuzione.dati.endDate)}
                    />
                    <TernaDelRisultato
                      dato={esecuzione.dati}
                      periodo={etichettaPeriodo(esecuzione.dati.startDate, esecuzione.dati.endDate)}
                    />
                    <RisultatoBacktest
                      dato={esecuzione.dati}
                      periodo={etichettaPeriodo(esecuzione.dati.startDate, esecuzione.dati.endDate)}
                    />
                  </>
                )}
              </div>
            )}
          </>
        </ContenitoreDati>
      </div>

      {/* La griglia in JSON, come la producono le pipeline di ricerca. Riempie i
          campi invece di essere un secondo modulo: dopo l'incollata il
          conteggio delle combinazioni e il minimo di warmup si aggiornano da
          soli, cosa che nel frontend precedente non succedeva. */}
      <Modale
        aperta={jsonAperto}
        onChiudi={() => setJsonAperto(false)}
        titolo="Griglia in JSON"
        sottotitolo="Un oggetto con una lista di valori per parametro"
        larga
        pieda={
          <>
            <Pulsante variante="tenue" onClick={() => setJsonAperto(false)}>
              Annulla
            </Pulsante>
            <Pulsante
              variante="primario"
              onClick={() => {
                const esito = leggiGrigliaJson(jsonTesto, strategia);
                if (esito.errore) {
                  setJsonErrore(esito.errore);
                  return;
                }
                setModulo((m) => ({ ...m, parametri: { ...m.parametri, ...esito.parametri } }));
                setJsonErrore(null);
                setJsonAperto(false);
              }}
            >
              Riempi i campi
            </Pulsante>
          </>
        }
      >
        <div className="sb-nota" style={{ marginBottom: 'var(--sb-s-5)' }}>
          Esempio: <code>{'{ "entryPeriod": [20, 55, 100], "exitPeriod": [10, 20] }'}</code>. I
          valori finiscono nei campi qui sotto, dove restano modificabili: le chiavi che non
          appartengono alla strategia scelta vengono rifiutate, perché il motore le ignorerebbe in
          silenzio.
        </div>
        <textarea
          className="sb-campo__input"
          rows={8}
          value={jsonTesto}
          onChange={(e) => setJsonTesto(e.target.value)}
          placeholder='{ "entryPeriod": [20, 55, 100] }'
          style={{
            width: '100%',
            background: 'var(--sb-superficie-2)',
            border: '1px solid var(--sb-bordo)',
            borderRadius: 'var(--sb-r-3)',
            padding: 'var(--sb-s-4)',
            fontFamily: 'var(--sb-font-numeri)',
            resize: 'vertical',
          }}
        />
        {jsonErrore && <div className="sb-campo__errore">{jsonErrore}</div>}
      </Modale>

      {/* Scelta fra 65: un foglio con la ricerca, non un menu lungo tre schermate. */}
      <Foglio
        aperta={foglioStrategie}
        onChiudi={() => setFoglioStrategie(false)}
        titolo="Strategia"
        sottotitolo={`${num(elencoStrategie.length)} strategie`}
      >
        <Campo
          etichetta="Cerca"
          testuale
          value={cerca}
          placeholder="Donchian, RSI, SEM…"
          onChange={(e) => setCerca(e.target.value)}
        />
        <div style={{ marginTop: 'var(--sb-s-5)' }}>
          <SelettoreStrategia
            voci={elencoStrategie}
            scelta={modulo.strategyName || null}
            onScegli={(nome) => {
              cambia({ strategyName: nome, parametri: {} });
              setFoglioStrategie(false);
            }}
          />
        </div>
      </Foglio>
    </div>
  );
}

/* ==========================================================================
 * PEZZI
 * ======================================================================== */

interface CoppiaDisponibile {
  exchangeId: number;
  asset: string;
  currency: string;
  dal: string;
  al: string;
  /** Più di uno significa che nel mezzo c'è un buco di candele. */
  intervalli: number;
}

function raggruppaPerCoppia(ranges: RangeDatiStorici[]): CoppiaDisponibile[] {
  const mappa = new Map<string, CoppiaDisponibile>();
  for (const r of ranges) {
    const chiave = `${r.exchangeId}|${r.asset}|${r.currency}`;
    const esistente = mappa.get(chiave);
    if (!esistente) {
      mappa.set(chiave, {
        exchangeId: r.exchangeId,
        asset: r.asset,
        currency: r.currency,
        dal: r.begin,
        al: r.end,
        intervalli: 1,
      });
      continue;
    }
    if (r.begin < esistente.dal) esistente.dal = r.begin;
    if (r.end > esistente.al) esistente.al = r.end;
    esistente.intervalli += 1;
  }
  return [...mappa.values()].sort((a, b) => `${a.asset}${a.currency}`.localeCompare(`${b.asset}${b.currency}`));
}

/* --------------------------------------------------------------------------
 * Finestre di periodo preimpostate
 *
 * Scegliere due date a mano è l'attrito peggiore del modulo, e le finestre che
 * si usano davvero sono sempre le stesse. «Tutto» arriva fino alla prima
 * candela realmente in magazzino per QUELLA coppia, letta dal server e non da
 * una costante: l'inizio dei dati non è lo stesso per tutti — BTC ed ETH
 * partono dal 17/08/2017, BNB dal 06/11/2017, XRP dal 04/05/2018 — e una data
 * scritta a mano qui sarebbe sbagliata per metà degli asset.
 * ------------------------------------------------------------------------ */

export type ChiavePeriodo = '6m' | '1a' | '2a' | '3a' | 'tutto';

const FINESTRE: { chiave: ChiavePeriodo; etichetta: string; mesi: number | null }[] = [
  { chiave: '6m', etichetta: 'Ultimi 6 mesi', mesi: 6 },
  { chiave: '1a', etichetta: '1 anno', mesi: 12 },
  { chiave: '2a', etichetta: '2 anni', mesi: 24 },
  { chiave: '3a', etichetta: '3 anni', mesi: 36 },
  { chiave: 'tutto', etichetta: 'Tutto', mesi: null },
];

function soloGiorno(iso: string): string {
  return iso.slice(0, 10);
}

function meno(mesi: number, da: Date): Date {
  const d = new Date(da.getTime());
  d.setUTCMonth(d.getUTCMonth() - mesi);
  return d;
}

function FinestrePeriodo({
  scelta,
  primaCandela,
  ultimaCandela,
  onScegli,
}: {
  scelta: ChiavePeriodo | null;
  primaCandela: string | null;
  ultimaCandela: string | null;
  onScegli: (chiave: ChiavePeriodo, dal: string, al: string) => void;
}) {
  /* La fine è l'ultima candela in magazzino, non oggi: chiedere un periodo che
     arriva a domani non aggiunge dati, aggiunge solo una data che non esiste. */
  const fine = ultimaCandela ? new Date(ultimaCandela) : new Date();

  const applica = (f: (typeof FINESTRE)[number]) => {
    if (f.mesi == null) {
      if (!primaCandela) return;
      onScegli(f.chiave, soloGiorno(primaCandela), soloGiorno(fine.toISOString()));
      return;
    }
    const inizio = meno(f.mesi, fine);
    /* Non si va oltre la prima candela: una finestra più lunga della storia
       disponibile misurerebbe un periodo che il magazzino non copre. */
    const limite = primaCandela ? new Date(primaCandela) : null;
    const dal = limite && inizio < limite ? limite : inizio;
    onScegli(f.chiave, soloGiorno(dal.toISOString()), soloGiorno(fine.toISOString()));
  };

  return (
    <div className="sb-campo">
      <div className="sb-campo__etichetta">
        <span className="sb-campo__nome">Finestra</span>
        <span className="sb-campo__suggerimento">
          {ultimaCandela ? `fino al ${soloGiorno(ultimaCandela)}` : 'fino a oggi'}
        </span>
      </div>
      <div className="sb-bt-pillole">
        {FINESTRE.map((f) => {
          const senzaDati = f.mesi == null && !primaCandela;
          return (
            <button
              key={f.chiave}
              type="button"
              className={`sb-bt-pillola${scelta === f.chiave ? ' sb-bt-pillola--scelta' : ''}`}
              aria-pressed={scelta === f.chiave}
              disabled={senzaDati}
              onClick={() => applica(f)}
            >
              {f.etichetta}
            </button>
          );
        })}
      </div>
      {!primaCandela && (
        <div className="sb-nota" style={{ marginTop: 'var(--sb-s-4)' }}>
          «Tutto» si accende dopo aver scelto una coppia in magazzino: l'inizio dei dati non è lo
          stesso per tutti gli asset, e va letto da quella coppia.
        </div>
      )}
    </div>
  );
}

/**
 * Quanta parte della finestra scelta si mangia il warmup.
 *
 * È l'errore che i periodi preimpostati rendono facile: con «ultimi 6 mesi» e
 * un warmup di 3.000 candele da un'ora — 125 giorni — resterebbero tre
 * settimane di periodo misurato, e il risultato sarebbe una manciata di
 * operazioni che sembra normale. Nessun errore, nessun avviso dal motore.
 */
function AvvisoFinestraMangiata({
  giorniWarmup,
  dal,
  al,
}: {
  giorniWarmup: number | null;
  dal: string;
  al: string;
}) {
  if (giorniWarmup == null || giorniWarmup <= 0 || !dal || !al) return null;
  const giorniFinestra = (new Date(al).getTime() - new Date(dal).getTime()) / (1000 * 60 * 60 * 24);
  if (!Number.isFinite(giorniFinestra) || giorniFinestra <= 0) return null;

  const quota = giorniWarmup / giorniFinestra;
  if (quota < 0.2) return null;

  const restano = Math.max(0, giorniFinestra - giorniWarmup);
  return (
    <Avvertenza livello={quota >= 0.5 ? 'grave' : 'cautela'}>
      Il warmup si mangia <b>{num(quota * 100, 0)}%</b> della finestra scelta:{' '}
      {num(giorniWarmup, 0)} giorni su {num(giorniFinestra, 0)}, quindi il periodo davvero misurato
      è di circa <b>{num(restano, 0)} giorni</b>. Su una finestra così corta le operazioni sono
      poche e le metriche non dicono niente — allunga il periodo o abbassa i parametri che
      richiedono la finestra più lunga.
    </Avvertenza>
  );
}

function ElencoCoppie({
  coppie,
  exchangeId,
  asset,
  currency,
  statoStorico,
  onScegli,
}: {
  coppie: CoppiaDisponibile[];
  exchangeId: string;
  asset: string;
  currency: string;
  statoStorico: string;
  onScegli: (c: CoppiaDisponibile) => void;
}) {
  if (statoStorico === 'caricamento') {
    return <div className="sb-nota">Leggo quali coppie sono in magazzino…</div>;
  }
  if (coppie.length === 0) {
    return (
      <div className="sb-nota">
        Il magazzino delle candele non ha risposto: la coppia si può scrivere a mano, ma senza
        candele il backtest non parte.
      </div>
    );
  }

  return (
    <div className="sb-campo">
      <div className="sb-campo__etichetta">
        <span className="sb-campo__nome">Coppie in magazzino</span>
        <span className="sb-campo__suggerimento">{coppie.length} con candele scaricate</span>
      </div>
      <div className="sb-bt-pillole">
        {coppie.map((c) => {
          const scelta =
            String(c.exchangeId) === exchangeId && c.asset === asset && c.currency === currency;
          return (
            <button
              key={`${c.exchangeId}-${c.asset}-${c.currency}`}
              type="button"
              className={`sb-bt-pillola${scelta ? ' sb-bt-pillola--scelta' : ''}`}
              aria-pressed={scelta}
              onClick={() => onScegli(c)}
            >
              {c.asset}/{c.currency}
              <span className="sb-bt-pillola__n">{etichettaPeriodo(c.dal, c.al)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Se il periodo chiesto è coperto dalle candele che ci sono davvero. */
function CoperturaCandele({
  coppia,
  dal,
  al,
}: {
  coppia: CoppiaDisponibile;
  dal: string;
  al: string;
}) {
  const inizioMancante = dal && dal < coppia.dal.slice(0, 10);
  const fineMancante = al && al > coppia.al.slice(0, 10);

  if (!inizioMancante && !fineMancante && coppia.intervalli === 1) {
    return (
      <div className="sb-nota">
        Candele in magazzino dal {dataOra(coppia.dal)} al {dataOra(coppia.al)}: il periodo chiesto è
        coperto.
      </div>
    );
  }

  return (
    <Avvertenza livello="cautela">
      {inizioMancante && (
        <>
          Le candele iniziano il {dataOra(coppia.dal)}, dopo la data di partenza chiesta: il
          backtest misurerebbe un periodo più corto.{' '}
        </>
      )}
      {fineMancante && (
        <>
          Le candele finiscono il {dataOra(coppia.al)}, prima della data di fine chiesta.{' '}
        </>
      )}
      {coppia.intervalli > 1 && (
        <>
          Il magazzino ha {coppia.intervalli} intervalli separati per questa coppia, quindi nel mezzo
          mancano candele: dove mancano, la strategia non vede prezzi e non opera.
        </>
      )}
    </Avvertenza>
  );
}

function PillolePeriodo({ valore, onScegli }: { valore: string; onScegli: (v: string) => void }) {
  return (
    <div className="sb-bt-pillole">
      {TIMEFRAME_COMUNI.map((m) => (
        <button
          key={m}
          type="button"
          className={`sb-bt-pillola${valore === String(m) ? ' sb-bt-pillola--scelta' : ''}`}
          aria-pressed={valore === String(m)}
          onClick={() => onScegli(String(m))}
        >
          {timeframe(m)}
        </button>
      ))}
    </div>
  );
}

function CampoParametro({
  chiave,
  predefinito,
  valore,
  modo,
  onCambia,
}: {
  chiave: string;
  predefinito: unknown;
  valore: string;
  modo: Modo;
  onCambia: (v: string) => void;
}) {
  const numerico = typeof predefinito === 'number';
  const valori = modo === 'gruppo' ? valoriDaTesto(valore, predefinito) : [];

  return (
    <Campo
      etichetta={chiave}
      testuale={!numerico || modo === 'gruppo'}
      /* In griglia il campo è testo anche per i parametri numerici: deve
         accettare «10, 20, 30» e «10..200:10», che un input numerico rifiuta. */
      type={numerico && modo === 'singolo' ? 'number' : 'text'}
      inputMode={numerico ? 'decimal' : undefined}
      value={valore}
      onChange={(e) => onCambia(e.target.value)}
      suggerimento={
        modo === 'gruppo'
          ? `${valori.length} ${valori.length === 1 ? 'valore' : 'valori'}${
              valori.length > 0 && valori.length <= 8 ? `: ${valori.join(', ')}` : ''
            }`
          : `predefinito ${String(predefinito)}`
      }
      errore={modo === 'gruppo' && valori.length === 0 ? 'Serve almeno un valore.' : null}
    />
  );
}

function AvvisoWarmup({
  minimo,
  cause,
  impostato,
  giorni,
  candleSize,
  onUsaMinimo,
}: {
  minimo: number;
  cause: { chiave: string; candele: number }[];
  impostato: number | null;
  giorni: number | null;
  candleSize: number | null;
  onUsaMinimo: () => void;
}) {
  const insufficiente = impostato != null && minimo > 0 && impostato < minimo;
  const giorniMinimo = candleSize ? giorniDiWarmup(minimo, candleSize) : null;

  return (
    <div className="sb-nuovo__warmup">
      {/* Questo avviso sta accanto al campo e non in fondo alla pagina: è
          l'errore che NON dà errore, e chi lo commette non torna a cercarlo. */}
      <Avvertenza livello={insufficiente ? 'grave' : 'nota'}>
        <b>Il warmup consuma l'inizio del periodo misurato.</b> Se è insufficiente il backtest non
        fallisce: produce un risultato completo, plausibile e falso. È già successo — con warmup
        2.400 invece di 2.700 lo stesso backtest dava 12,56% invece di 10,57%, cioè sembrava
        migliore.
        {minimo > 0 && (
          <>
            {' '}
            Questi parametri ne chiedono almeno <b>{num(minimo)} candele</b>
            {cause.length > 0 && ` (${cause[0].chiave} = ${num(cause[0].candele)})`}
            {giorniMinimo != null && `, cioè ${num(giorniMinimo, 0)} giorni di storia`}.
          </>
        )}
        {minimo === 0 && ' Da questi parametri non si deduce nessun minimo: nessuno di essi ha un nome che indichi una finestra.'}
        {insufficiente && (
          <>
            {' '}
            <b>
              Il valore impostato ({num(impostato)}) è sotto il minimo: la strategia resterebbe
              inerte per le prime {num(minimo - impostato)} candele dentro il periodo misurato.
            </b>
          </>
        )}
      </Avvertenza>

      <div className="sb-nuovo__warmup-azioni">
        {minimo > 0 && (
          <Pulsante variante={insufficiente ? 'cautela' : 'tenue'} onClick={onUsaMinimo}>
            Usa il minimo ({num(minimo)})
          </Pulsante>
        )}
        {giorni != null && giorni > 0 && (
          <span className="sb-nota">
            Il warmup impostato toglie circa {num(giorni, 0)} giorni all'inizio del periodo.
          </span>
        )}
      </div>
    </div>
  );
}

function RigheTimeframe({
  righe,
  minimo,
  onCambia,
}: {
  righe: RigaTimeframe[];
  minimo: number;
  onCambia: (righe: RigaTimeframe[]) => void;
}) {
  return (
    <div className="sb-nuovo__timeframe">
      <div className="sb-nota">
        Ogni riga è una coppia candela + warmup, e la griglia le prova tutte: le combinazioni dei
        parametri si moltiplicano per il numero di righe.
      </div>

      {righe.map((r, i) => (
        <div className="sb-nuovo__timeframe-riga" key={i}>
          <Campo
            etichetta="Candela"
            unita="min"
            type="number"
            min={1}
            inputMode="numeric"
            value={r.candleSize}
            onChange={(e) =>
              onCambia(righe.map((x, j) => (i === j ? { ...x, candleSize: e.target.value } : x)))
            }
          />
          <Campo
            etichetta="Warmup"
            unita="candele"
            type="number"
            min={0}
            inputMode="numeric"
            suggerimento={minimo > 0 ? `minimo ${num(minimo)}` : undefined}
            value={r.warmupPeriod}
            onChange={(e) =>
              onCambia(righe.map((x, j) => (i === j ? { ...x, warmupPeriod: e.target.value } : x)))
            }
          />
          <Pulsante
            variante="tenue"
            disabled={righe.length === 1}
            onClick={() => onCambia(righe.filter((_, j) => j !== i))}
            aria-label={`Togli la riga ${i + 1}`}
          >
            Togli
          </Pulsante>
        </div>
      ))}

      <Pulsante
        variante="tenue"
        onClick={() =>
          onCambia([...righe, { candleSize: righe[righe.length - 1]?.candleSize ?? '60', warmupPeriod: String(minimo || '') }])
        }
      >
        Aggiungi una candela
      </Pulsante>
    </div>
  );
}

function ScelteCosti({ onApplica }: { onApplica: (v: (typeof VENUE)[number]) => void }) {
  const [aperto, setAperto] = useState<string | null>(null);

  return (
    <div className="sb-campo">
      <div className="sb-campo__etichetta">
        <span className="sb-campo__nome">Costi misurati</span>
        <span className="sb-campo__suggerimento">quote reali, non stime</span>
      </div>
      <div className="sb-bt-pillole">
        {VENUE.map((v) => (
          <button
            key={v.id}
            type="button"
            className="sb-bt-pillola"
            onClick={() => {
              onApplica(v);
              setAperto(v.id);
            }}
          >
            {v.nome}
            <span className="sb-bt-pillola__n">{num(v.bp, 2)} bp</span>
          </button>
        ))}
      </div>
      {/* La nota del venue è scritta sotto, non in un tooltip: su telefono il
          passaggio del mouse non esiste, e questa nota dice perché quel numero
          è quello giusto. */}
      {aperto && (
        <div className="sb-nota" style={{ marginTop: 'var(--sb-s-4)' }}>
          {VENUE.find((v) => v.id === aperto)?.nota}
        </div>
      )}
    </div>
  );
}

function ConteggioCombinazioni({
  conteggio,
}: {
  conteggio: { totale: number; perParametro: { chiave: string; quanti: number }[]; righe: number };
}) {
  const molte = conteggio.totale > COMBINAZIONI_MOLTE;
  const troppe = conteggio.totale > COMBINAZIONI_TROPPE;
  const scomposizione = conteggio.perParametro
    .filter((p) => p.quanti !== 1)
    .map((p) => `${p.chiave} ×${p.quanti}`)
    .join(' · ');

  return (
    <div className={`sb-nuovo__conteggio${molte ? ' sb-nuovo__conteggio--molte' : ''}`}>
      <span className="sb-nuovo__conteggio-numero">{num(conteggio.totale)}</span>
      <span className="sb-nuovo__conteggio-testo">
        backtest da eseguire — è il prodotto di tutti i valori:{' '}
        {scomposizione || 'un valore per parametro'} × {conteggio.righe}{' '}
        {conteggio.righe === 1 ? 'candela' : 'candele'}.
        {troppe && (
          <>
            {' '}
            <b>
              Oltre {num(COMBINAZIONI_TROPPE)} combinazioni: quasi sempre è un valore di troppo in un
              campo, non una griglia voluta.
            </b>
          </>
        )}
        {molte && !troppe && (
          <> Una griglia di questa dimensione occupa il motore a lungo: vale la pena rileggerla.</>
        )}
      </span>
    </div>
  );
}

/** Segue una griglia appena lanciata: quante righe ha già prodotto. */
function SeguiGruppo({ id, previsti }: { id: number; previsti: number | null }) {
  const navigate = useNavigate();
  const fatti = usaRichiesta(
    (segnale) => apiBacktest.cerca({ backtestGroupId: id, limit: 1 }, segnale),
    [id],
    { eVuoto: () => false },
  );

  const ricarica = fatti.ricarica;
  useEffect(() => {
    /* Dieci secondi e non cinque: una griglia dura ore, e la domanda qui è
       «sta andando avanti?», non «è finita?». */
    const t = setInterval(ricarica, 10000);
    return () => clearInterval(t);
  }, [ricarica]);

  const eseguiti = fatti.dati?.total ?? null;

  return (
    <div className="sb-bt-riquadro">
      <div className="sb-bt-riquadro__testata">
        <span className="sb-bt-riquadro__titolo">Griglia #{id} avviata</span>
        <Distintivo tono="cautela">in corso</Distintivo>
      </div>
      <div className="sb-nota">
        {eseguiti == null
          ? 'Conto quante righe ha prodotto…'
          : `${num(eseguiti)} backtest già salvati${previsti != null ? ` su ${num(previsti)} previsti` : ''}. Il conteggio si aggiorna ogni dieci secondi.`}
      </div>
      {/* Navigazione interna e non `<a href>`: un ricaricamento completo qui
          butterebbe via il modulo appena compilato, che serve ancora se la
          griglia va rilanciata con un valore diverso. */}
      <div style={{ marginTop: 'var(--sb-s-5)', display: 'flex', gap: 'var(--sb-s-4)', flexWrap: 'wrap' }}>
        <Pulsante variante="primario" onClick={() => navigate(`/gruppi/${id}`)}>
          Apri il gruppo
        </Pulsante>
        <Pulsante onClick={() => navigate(`/backtest?backtestGroupId=${id}`)}>
          Vedi le righe prodotte
        </Pulsante>
      </div>
    </div>
  );
}

/* ==========================================================================
 * CORPI DELLE RICHIESTE E VALIDAZIONE
 * ======================================================================== */

function parametriSingoli(
  modulo: Modulo,
  strategia: StrategiaDisponibile,
): Record<string, unknown> {
  const fuori: Record<string, unknown> = {};
  for (const [chiave, predefinito] of Object.entries(strategia.defaultParams)) {
    fuori[chiave] = valoreDaTesto(modulo.parametri[chiave] ?? String(predefinito), predefinito);
  }
  return fuori;
}

function corpoSingolo(modulo: Modulo, strategia: StrategiaDisponibile): AvvioBacktest {
  return {
    exchangeId: Number(modulo.exchangeId),
    asset: modulo.asset.trim(),
    currency: modulo.currency.trim(),
    startDate: modulo.startDate,
    endDate: modulo.endDate,
    strategyName: strategia.name,
    strategyParams: parametriSingoli(modulo, strategia),
    warmupPeriod: numeroDa(modulo.warmupPeriod) ?? 0,
    candleSize: numeroDa(modulo.candleSize) ?? 0,
    assetBalance: numeroDa(modulo.assetBalance) ?? 0,
    currencyBalance: numeroDa(modulo.currencyBalance) ?? 0,
    feeMode: modulo.feeMode,
    /* Le commissioni si mandano in PERCENTUALE: il server divide per cento.
       Lo slippage no, resta percentuale fino al trader. */
    makerFee: numeroDa(modulo.makerFee) ?? 0,
    takerFee: numeroDa(modulo.takerFee) ?? 0,
    slippage: numeroDa(modulo.slippage) ?? 0,
    force: modulo.force,
  };
}

function corpoGruppo(modulo: Modulo, strategia: StrategiaDisponibile): AvvioGruppoBacktest {
  /* OGNI parametro dev'essere una lista, anche con un valore solo: il comando
     di griglia legge `vals.length` su ciascuno, e uno scalare lo farebbe
     fallire a metà esecuzione — dopo aver già creato il gruppo. */
  const parametri: Record<string, unknown> = {};
  for (const [chiave, predefinito] of Object.entries(strategia.defaultParams)) {
    parametri[chiave] = valoriDaTesto(modulo.parametri[chiave] ?? String(predefinito), predefinito);
  }

  const facoltativo = (v: string): number | undefined => numeroDa(v) ?? undefined;

  return {
    exchangeId: Number(modulo.exchangeId),
    asset: modulo.asset.trim(),
    currency: modulo.currency.trim(),
    startDate: modulo.startDate,
    endDate: modulo.endDate,
    strategyName: strategia.name,
    strategyParams: parametri,
    timeframeArray: modulo.timeframe.map((r) => ({
      candleSize: numeroDa(r.candleSize) ?? 0,
      warmupPeriod: numeroDa(r.warmupPeriod) ?? 0,
    })),
    maxDrawdown: facoltativo(modulo.maxDrawdown),
    minWinRate: facoltativo(modulo.minWinRate),
    maxUnderperformance: facoltativo(modulo.maxUnderperformance),
    maxTradesPerDay: facoltativo(modulo.maxTradesPerDay),
    earlyStopCheckpoint: facoltativo(modulo.earlyStopCheckpoint),
  };
}

function validazione(
  modulo: Modulo,
  strategia: StrategiaDisponibile | null,
  combinazioni: number | null,
): string[] {
  const errori: string[] = [];
  if (!modulo.exchangeId) errori.push('l’exchange');
  if (!modulo.asset.trim() || !modulo.currency.trim()) errori.push('la coppia');
  if (!modulo.startDate || !modulo.endDate) errori.push('il periodo');
  else if (modulo.startDate >= modulo.endDate) errori.push('una data di fine dopo quella di inizio');
  if (!strategia) errori.push('la strategia');

  if (modulo.modo === 'singolo') {
    if ((numeroDa(modulo.candleSize) ?? 0) <= 0) errori.push('la dimensione della candela');
    if (numeroDa(modulo.warmupPeriod) == null) errori.push('il warmup');
    if (numeroDa(modulo.currencyBalance) == null) errori.push('il capitale in valuta');
  } else {
    if (modulo.timeframe.length === 0) errori.push('almeno una candela');
    if (modulo.timeframe.some((r) => (numeroDa(r.candleSize) ?? 0) <= 0)) {
      errori.push('una candela valida in ogni riga');
    }
    if (modulo.timeframe.some((r) => numeroDa(r.warmupPeriod) == null)) {
      errori.push('il warmup in ogni riga');
    }
    if (combinazioni != null && combinazioni <= 0) errori.push('almeno un valore per parametro');
  }

  return errori;
}
